import React, { useState } from 'react';
import client from '../../api/client';
import usePolling from '../../hooks/usePolling';
import MetricCard from '../common/MetricCard';
import MetricGrid from '../common/MetricGrid';
import StatusBadge from '../common/StatusBadge';
import TimeRangeSelector from '../common/TimeRangeSelector';
import TimeSeriesChart from '../charts/TimeSeriesChart';
import GaugeChart from '../charts/GaugeChart';
import CellHeatmap from '../charts/CellHeatmap';
import LoadingSpinner from '../common/LoadingSpinner';
import colors from '../../theme/colors';

const PACKS = ['battery2', 'battery3', 'battery4', 'battery6', 'battery7', 'battery8'];

function notifyUpdate() {
  window.dispatchEvent(new Event('sv360:data-update'));
}

export default function BatteriesPage() {
  const [selectedPack, setSelectedPack] = useState(null); // null = all packs
  const [range, setRange] = useState('-1h');

  const { data: latest, loading } = usePolling(async () => {
    const res = await client.get('/api/batteries/latest');
    notifyUpdate();
    return res.data.packs;
  }, 5000);

  const { data: history } = usePolling(async () => {
    if (selectedPack) {
      const res = await client.get(`/api/batteries/history?range=${range}&packs=${selectedPack}&fields=SoC,Voltage,SmoothCurrent`);
      return res.data.series;
    }
    const res = await client.get(`/api/batteries/history?range=${range}&fields=SoC,Voltage`);
    return res.data.series;
  }, 10000, [range, selectedPack]);

  const { data: cells } = usePolling(async () => {
    if (!selectedPack) return null;
    const res = await client.get(`/api/batteries/${selectedPack}/cells`);
    return res.data.cells;
  }, 10000, [selectedPack]);

  if (loading && !latest) return <LoadingSpinner />;

  return (
    <div>
      {/* Sub-nav */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        <TabButton label="All Packs" active={!selectedPack} onClick={() => setSelectedPack(null)} />
        {PACKS.map(p => (
          <TabButton key={p} label={p.replace('battery', 'Bat ')} active={selectedPack === p} onClick={() => setSelectedPack(p)} />
        ))}
      </div>

      {!selectedPack ? (
        <AllPacksView latest={latest} history={history} range={range} setRange={setRange} />
      ) : (
        <SinglePackView pack={selectedPack} data={latest?.[selectedPack]} history={history} cells={cells} range={range} setRange={setRange} />
      )}
    </div>
  );
}

function AllPacksView({ latest, history, range, setRange }) {
  // Build SoC history series for overlay chart
  const socSeries = {};
  if (history) {
    for (const [pack, fields] of Object.entries(history)) {
      if (fields.SoC) {
        socSeries[pack.replace('battery', 'Bat')] = fields.SoC;
      }
    }
  }

  return (
    <div>
      {/* Pack summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
        {PACKS.map(p => {
          const b = latest?.[p];
          return (
            <div key={p} className="card" style={{ padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>
                  {p.replace('battery', 'Battery ')}
                </span>
                <StatusBadge
                  label={b ? 'Online' : 'Offline'}
                  color={b ? 'green' : 'gray'}
                />
              </div>
              <GaugeChart value={b?.SoC} height={120} />
              <MetricGrid minWidth={80}>
                <MetricCard label="Voltage" value={b?.Voltage} unit="V" />
                <MetricCard label="Current" value={b?.SmoothCurrent} unit="A" />
                <MetricCard label="Temp" value={b?.CellTempMax} unit="°C" decimals={0} />
              </MetricGrid>
            </div>
          );
        })}
      </div>

      {/* Historical SoC chart */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span className="card-title" style={{ marginBottom: 0 }}>SoC History</span>
          <TimeRangeSelector selected={range} onChange={setRange} />
        </div>
        <TimeSeriesChart
          series={socSeries}
          config={{
            yAxisLabel: 'SoC (%)',
            height: 300,
          }}
        />
      </div>
    </div>
  );
}

function SinglePackView({ pack, data, history, cells, range, setRange }) {
  const packSeries = history?.[pack] || {};

  return (
    <div>
      {/* BMS metrics */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">{pack.replace('battery', 'Battery ')} — BMS Data</div>
        <MetricGrid minWidth={140}>
          <MetricCard label="SoC" value={data?.SoC} unit="%" />
          <MetricCard label="SoC Avg" value={data?.SoCavg} unit="%" />
          <MetricCard label="Voltage" value={data?.Voltage} unit="V" />
          <MetricCard label="Current" value={data?.SmoothCurrent} unit="A" />
          <MetricCard label="Cell V Max" value={data?.CellVoltageMax} unit="V" decimals={3} />
          <MetricCard label="Cell V Min" value={data?.CellVoltageMin} unit="V" decimals={3} />
          <MetricCard label="Cell T Max" value={data?.CellTempMax} unit="°C" decimals={0} />
          <MetricCard label="Cell T Min" value={data?.CellTempMin} unit="°C" decimals={0} />
          <MetricCard label="Max Charge I" value={data?.MaxChargeCurrent} unit="A" decimals={0} />
          <MetricCard label="Max Discharge I" value={data?.MaxDischargeCurrent} unit="A" decimals={0} />
          <MetricCard label="Beg of Life" value={data?.BegofLife} unit="%" decimals={0} />
          <MetricCard label="Pyro Fuse" value={data?.PyroFuseBlown === 1 ? 'BLOWN' : 'OK'} decimals={0}
            status={data?.PyroFuseBlown === 1 ? 'danger' : 'normal'} />
        </MetricGrid>
      </div>

      {/* Cell Heatmap */}
      <div className="card" style={{ marginBottom: 16 }}>
        <CellHeatmap cells={cells} title={`${pack.replace('battery', 'Battery ')} — Cell Voltages`} />
      </div>

      {/* Historical charts */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span className="card-title" style={{ marginBottom: 0 }}>History</span>
          <TimeRangeSelector selected={range} onChange={setRange} />
        </div>
        <TimeSeriesChart
          series={packSeries}
          config={{
            height: 300,
            seriesConfig: {
              SoC: { name: 'SoC', unit: '%', color: colors.series[0] },
              Voltage: { name: 'Voltage', unit: 'V', color: colors.series[1], yAxis: 1 },
              SmoothCurrent: { name: 'Current', unit: 'A', color: colors.series[2], yAxis: 1 },
            },
            yAxes: [
              { title: { text: 'SoC (%)' } },
              { title: { text: 'V / A' } },
            ],
          }}
        />
      </div>
    </div>
  );
}

function TabButton({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 16px',
      borderRadius: 6,
      border: 'none',
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      background: active ? colors.accent : colors.surface,
      color: active ? colors.bg : colors.textMuted,
    }}>
      {label}
    </button>
  );
}
